import { Phone, ShieldCheck, Clock, Lock, CalendarCheck } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { HeroBackground } from "@/components/site/HeroBackground";
import { PageSEO } from "@/components/site/PageSEO";
import { Reveal } from "@/components/motion/Reveal";
import { stockImages } from "@/data/site/stockImages";
import { brand } from "@/data/site/brand";
import { BookingCalendar } from "@/components/site/BookingCalendar";

const path = "/schedule";

const serviceLd = {
  "@context": "https://schema.org",
  "@type": "AccountingService",
  name: `Schedule a Free Consultation — ${brand.shortName}`,
  description:
    "Book a free, confidential consultation with a CPA. Pick a time that works for you.",
  url: `${brand.siteUrl}${path}`,
  telephone: brand.phoneTel,
  areaServed: ["United States"],
};

const Schedule = () => (
  <SiteLayout>
    <PageSEO
      title="Schedule a Free Consultation"
      description={`Pick a time to speak with a CPA about your IRS problem. Free, confidential, no obligation. Or call ${brand.phone}.`}
      path={path}
      noindex
      image={stockImages.consultation.src}
      breadcrumbs={[
        { name: "Home", href: "/" },
        { name: "Schedule", href: path },
      ]}
      jsonLd={[serviceLd]}
    />

    <section className="relative bg-gradient-navy text-white overflow-hidden">
      <HeroBackground image={stockImages.consultation} priority />
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-10 lg:py-20 max-w-5xl">
        <Reveal>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-4">
            Free · Confidential · No obligation
          </p>
          <h1 className="font-heading text-4xl lg:text-5xl font-bold leading-[1.05] mb-5 [text-wrap:balance]">
            Schedule your <span className="text-gold">free consultation.</span>
          </h1>
          <p className="text-lg text-white/80 max-w-2xl leading-relaxed mb-6">
            Pick a time below and you'll get a confirmation email with a calendar invite.
            Prefer to talk right now? Call{" "}
            <a href={`tel:${brand.phoneTel}`} className="font-semibold text-gold underline-offset-4 hover:underline">
              {brand.phone}
            </a>
            .
          </p>
          <div className="flex flex-wrap gap-4 text-sm text-white/80">
            <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4 text-gold" /> Same-day response</span>
            <span className="inline-flex items-center gap-2"><Lock className="w-4 h-4 text-gold" /> IRS §7216 confidential</span>
            <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-gold" /> No obligation</span>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="py-12 lg:py-20 bg-secondary/40 border-t border-border">
      <div className="container mx-auto px-4 max-w-4xl">
        <Reveal>
          <div className="flex items-center justify-center gap-2 mb-6 text-navy">
            <CalendarCheck className="w-5 h-5 text-gold" />
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold">Pick a time</p>
          </div>
          <div className="rounded-lg bg-white border border-border shadow-sm overflow-hidden">
            <BookingCalendar minHeight={820} />
          </div>
          <p className="text-xs text-muted-foreground text-center mt-4">
            Trouble with the calendar?{" "}
            <a href={`tel:${brand.phoneTel}`} className="font-semibold text-navy hover:text-gold underline-offset-4 hover:underline">
              Call {brand.phone}
            </a>{" "}
            and we'll get you booked in under a minute.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="py-12 bg-white border-t border-border">
      <div className="container mx-auto px-4 max-w-3xl text-center">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-2">Prefer to call</p>
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy mb-4">
          A CPA answers the phone.
        </h2>
        <a
          href={`tel:${brand.phoneTel}`}
          className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-navy font-bold px-7 py-4 rounded text-lg transition-transform hover:scale-[1.03]"
        >
          <Phone className="w-5 h-5" /> Call {brand.phone}
        </a>
        <p className="text-[11px] text-muted-foreground mt-3">{brand.hours}</p>
      </div>
    </section>
  </SiteLayout>
);

export default Schedule;
